import { useCallback } from 'react';
import { useAuthContext } from '@/context/AuthContext';
import type { ChatbotStopRequest } from '@/models/chatbot';
import { fetchWithAuthRetry } from './withAuthRetry';
import { getChatbotApiBaseUrl, readChatbotErrorMessage } from './chatbotApiRequest';

class StopError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'StopError';
    this.status = status;
  }
}

function isStopError(value: unknown): value is StopError {
  return value instanceof StopError;
}

function buildStopUrl(baseUrl: string, conversationId: string): string {
  return `${baseUrl}/chatbot/conversations/${encodeURIComponent(conversationId)}/stop`;
}

async function postStopRequest(url: string, request: ChatbotStopRequest, authToken: string): Promise<Response> {
  const payload = request.session_id ? { session_id: request.session_id } : {};

  return fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify(payload),
  });
}

interface UseChatbotStopReturn {
  stopStream: (request: ChatbotStopRequest) => Promise<void>;
}

const useChatbotStop = (): UseChatbotStopReturn => {
  const { token, ensureValidSession } = useAuthContext();

  const stopStream = useCallback(
    async (request: ChatbotStopRequest): Promise<void> => {
      const baseUrl = getChatbotApiBaseUrl();
      if (!baseUrl) {
        throw new StopError(0, 'Chatbot API URL is not configured');
      }

      const url = buildStopUrl(baseUrl, request.conversation_id);

      const response = await fetchWithAuthRetry(
        authToken => postStopRequest(url, request, authToken),
        { token, ensureValidSession },
        (status, message) => new StopError(status, message),
      );

      if (!response.ok) {
        const errorMessage = await readChatbotErrorMessage(response);
        throw new StopError(response.status, errorMessage);
      }
    },
    [ensureValidSession, token],
  );

  return { stopStream };
};

export { isStopError };
export type { StopError };
export default useChatbotStop;
